import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

export const ADJUST_REASONS = [
  { value: "correction", label: "Correction (default)" },
  { value: "count", label: "Count" },
  { value: "received", label: "Received" },
  { value: "return_restock", label: "Return restock" },
  { value: "damaged", label: "Damaged" },
  { value: "theft_or_loss", label: "Theft or loss" },
  { value: "promotion_or_donation", label: "Promotion or donation" },
  { value: "other", label: "Other" },
] as const
export type AdjustReason = (typeof ADJUST_REASONS)[number]["value"]
export const reasonLabel = (r: string) => ADJUST_REASONS.find((x) => x.value === r)?.label.replace(" (default)", "") ?? r

export type InventoryRow = {
  id: string
  sku: string | null
  title: string | null
  product_id?: string
  product_title?: string
  variant_title?: string
  thumbnail?: string | null
  on_hand: number
  committed: number
  available: number
  has_level: boolean
}

export function useInventory(p: { q?: string; locationId?: string; limit: number; offset: number; ids?: string[] | null }) {
  return useQuery({
    queryKey: ["inventory", p],
    enabled: !!p.locationId && (p.ids === undefined || p.ids !== null),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await sdk.admin.inventoryItem.list({
        q: p.q?.trim() || undefined,
        ...(p.ids ? { id: p.ids.length ? p.ids : ["iitem_none"] } : {}),
        limit: p.limit,
        offset: p.offset,
        order: "sku",
        fields: "id,sku,title,thumbnail,*location_levels,*variants,*variants.product",
      } as any)
      const rows: InventoryRow[] = res.inventory_items.map((i: any) => {
        const lvl = (i.location_levels ?? []).find((l: any) => l.location_id === p.locationId)
        const v = i.variants?.[0]
        const on_hand = Number(lvl?.stocked_quantity ?? 0)
        const committed = Number(lvl?.reserved_quantity ?? 0)
        return {
          id: i.id,
          sku: i.sku,
          title: i.title,
          product_id: v?.product?.id ?? v?.product_id,
          product_title: v?.product?.title ?? i.title ?? undefined,
          variant_title: v?.title,
          thumbnail: v?.product?.thumbnail ?? i.thumbnail,
          on_hand,
          committed,
          available: on_hand - committed,
          has_level: !!lvl,
        }
      })
      return { rows, count: res.count }
    },
  })
}

export type AdjustInput = { location_id: string; reason: AdjustReason; note?: string; changes: { inventory_item_id: string; stocked_quantity: number }[] }

export function adjustInventory(body: AdjustInput) {
  return sdk.client.fetch<{ adjusted: number }>("/admin/terify/inventory/adjust", { method: "POST", body })
}

export function useAdjustInventory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: adjustInventory,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] })
      qc.invalidateQueries({ queryKey: ["product"] })
      qc.invalidateQueries({ queryKey: ["activity"] })
    },
  })
}

/** Days of cover per inventory item (GET /admin/terify/inventory/cover): available ÷ trailing 14-day average daily units sold. */
export type CoverRow = { inventory_item_id: string; available: number; units_sold_14d: number; avg_daily: number; days_of_cover: number | null; low: boolean }
export function useCover() {
  return useQuery({
    queryKey: ["inventory", "cover"],
    queryFn: () => sdk.client.fetch<{ flag_days: number; trailing_days: number; rows: CoverRow[] }>("/admin/terify/inventory/cover"),
    staleTime: 60_000,
  })
}
